import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AppWindow,
  Calculator,
  ShieldCheck,
  ExternalLink,
  Plus,
  Trash2,
  Power,
  PowerOff,
  RefreshCw,
  Loader2,
  AlertCircle,
  Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from './ui/dialog';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Calculator,
  ShieldCheck,
  AppWindow,
  Globe
};

const COLOR_MAP = {
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' }
};

const ConnectedApps = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    url: '',
    description: '',
    category: 'external',
    icon: 'AppWindow',
    color: 'blue'
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/connected-apps`);
      setApps(res.data);
      if (res.data.length && !activeSlug) {
        setActiveSlug(res.data[0].slug);
      }
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeApp = useMemo(
    () => apps.find((a) => a.slug === activeSlug) || null,
    [apps, activeSlug]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name || !form.slug || !form.url) {
      setFormError('Name, slug and URL are required.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/connected-apps`, {
        ...form,
        embeddable: true,
        enabled: true
      });
      setApps((prev) => [...prev, res.data]);
      setActiveSlug(res.data.slug);
      setCreateOpen(false);
      setForm({
        name: '',
        slug: '',
        url: '',
        description: '',
        category: 'external',
        icon: 'AppWindow',
        color: 'blue'
      });
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Could not create app');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (slug, enabled) => {
    try {
      const res = await axios.put(`${API}/connected-apps/${slug}`, { enabled: !enabled });
      setApps((prev) => prev.map((a) => (a.slug === slug ? res.data : a)));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Remove this connected app?')) return;
    try {
      await axios.delete(`${API}/connected-apps/${slug}`);
      const next = apps.filter((a) => a.slug !== slug);
      setApps(next);
      if (activeSlug === slug) {
        setActiveSlug(next[0]?.slug || null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const reloadIframe = () => {
    setIframeLoading(true);
    setIframeKey((k) => k + 1);
  };

  const renderIcon = (iconName, className) => {
    const Icon = ICON_MAP[iconName] || AppWindow;
    return <Icon className={className} />;
  };

  return (
    <div className="flex-1 overflow-hidden bg-gray-50 flex flex-col" data-testid="connected-apps-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-5 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1 tracking-wider uppercase">Workspaces</p>
          <h1 className="text-2xl font-bold text-gray-900">Connected Apps</h1>
          <p className="text-sm text-gray-600 mt-1">
            Embedded full-functionality access to integrated platforms.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchApps}
            data-testid="refresh-apps-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-app-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add App
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle>Connect a new app</DialogTitle>
                <DialogDescription>
                  Register a website or web app. It will be embedded in this dashboard.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="app-name">Name</Label>
                  <Input
                    id="app-name"
                    data-testid="app-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. ZeroError RegTech"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="app-slug">Slug</Label>
                    <Input
                      id="app-slug"
                      data-testid="app-slug-input"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="zeroerror-regtech"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="app-color">Accent</Label>
                    <select
                      id="app-color"
                      data-testid="app-color-select"
                      className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                    >
                      {Object.keys(COLOR_MAP).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app-url">URL</Label>
                  <Input
                    id="app-url"
                    data-testid="app-url-input"
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app-desc">Description</Label>
                  <Textarea
                    id="app-desc"
                    data-testid="app-desc-input"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What does this app do?"
                  />
                </div>
                {formError && (
                  <div className="flex items-start space-x-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-app-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 grid grid-cols-12 gap-0 overflow-hidden">
        {/* Sidebar list */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-3 border-r border-gray-200 bg-white overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading apps...
            </div>
          )}
          {error && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}
          {!loading && !apps.length && (
            <div className="p-8 text-center text-gray-500 text-sm">
              No connected apps yet. Click <span className="font-medium">Add App</span> to begin.
            </div>
          )}
          <div className="p-3 space-y-2">
            {apps.map((appItem) => {
              const palette = COLOR_MAP[appItem.color] || COLOR_MAP.blue;
              const isActive = appItem.slug === activeSlug;
              return (
                <div
                  key={appItem.slug}
                  data-testid={`app-card-${appItem.slug}`}
                  className={cn(
                    'group relative rounded-lg border p-3 cursor-pointer transition-all',
                    isActive
                      ? `${palette.border} ${palette.bg} shadow-sm`
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  )}
                  onClick={() => {
                    setActiveSlug(appItem.slug);
                    setIframeLoading(true);
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                        palette.bg,
                        palette.border,
                        'border'
                      )}
                    >
                      {renderIcon(appItem.icon, cn('w-5 h-5', palette.text))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {appItem.name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {appItem.url.replace(/^https?:\/\//, '')}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span
                          className={cn(
                            'inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                            appItem.enabled
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          )}
                        >
                          <span
                            className={cn(
                              'w-1.5 h-1.5 rounded-full mr-1',
                              appItem.enabled ? 'bg-green-500' : 'bg-gray-400'
                            )}
                          />
                          {appItem.enabled ? 'Live' : 'Disabled'}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {appItem.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      data-testid={`toggle-app-${appItem.slug}`}
                      title={appItem.enabled ? 'Disable' : 'Enable'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(appItem.slug, appItem.enabled);
                      }}
                      className="p-1 rounded hover:bg-white border border-gray-200"
                    >
                      {appItem.enabled ? (
                        <Power className="w-3.5 h-3.5 text-green-600" />
                      ) : (
                        <PowerOff className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                    <button
                      data-testid={`delete-app-${appItem.slug}`}
                      title="Remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(appItem.slug);
                      }}
                      className="p-1 rounded hover:bg-white border border-gray-200"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Iframe viewer */}
        <main className="col-span-12 md:col-span-9 lg:col-span-9 bg-gray-100 flex flex-col overflow-hidden">
          {!activeApp ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select an app from the left to launch it.
            </div>
          ) : (
            <>
              <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  {renderIcon(
                    activeApp.icon,
                    cn('w-5 h-5', (COLOR_MAP[activeApp.color] || COLOR_MAP.blue).text)
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {activeApp.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{activeApp.url}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" onClick={reloadIframe} data-testid="reload-iframe-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Reload
                  </Button>
                  <a
                    href={activeApp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-external-link"
                  >
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4 mr-1.5" />
                      Open in new tab
                    </Button>
                  </a>
                </div>
              </div>

              {!activeApp.enabled ? (
                <div className="flex-1 flex items-center justify-center p-8">
                  <Card className="max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <PowerOff className="w-5 h-5 text-gray-500" />
                        <span>App disabled</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        Enable this app to load its embedded view.
                      </p>
                      <Button onClick={() => handleToggle(activeApp.slug, activeApp.enabled)}>
                        <Power className="w-4 h-4 mr-2" />
                        Enable
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="flex-1 relative bg-white">
                  {iframeLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                    </div>
                  )}
                  <iframe
                    key={`${activeApp.slug}-${iframeKey}`}
                    title={activeApp.name}
                    src={activeApp.url}
                    onLoad={() => setIframeLoading(false)}
                    className="w-full h-full border-0"
                    sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-downloads"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid={`iframe-${activeApp.slug}`}
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ConnectedApps;
